const DECISION_KEYWORDS = [
  'starting level',
  'beginning level',
  'beginning color',
  'desired level',
  'desired color',
  'lift',
  'lifting',
  'tone',
  'toning',
  'orange',
  'brassy',
  'color correction',
  'correction',
  'going from',
  'take my client from',
];

// Matches patterns like "level 4 to level 8" or "from a 5 to a 10"
const LEVEL_PATTERN = /(?:level|lvl)?\s*\d+\s*to\s*(?:a\s*)?(?:level|lvl)?\s*\d+/i;
const FROM_PATTERN = /from\s+(?:a\s+)?\d+\s+to\s+(?:a\s+)?\d+/i;

export function detectProDecisionIntent(input: string): boolean {
  const lower = input.toLowerCase();
  if (DECISION_KEYWORDS.some((kw) => lower.includes(kw))) return true;
  if (LEVEL_PATTERN.test(input) || FROM_PATTERN.test(input)) return true;
  return false;
}

/* ────────────────────────────────────────────────────────────────
   Upgrade-intent detection
   Behavior-based routing for the upgrade flow:
     - "money"  → Pro Competitor ($97/mo)  — income / clients / business / pricing
     - "skill"  → Pro Stylist    ($67/mo)  — color / cuts / formulas / consultations
     - null     → no signal yet (default Stylist $47/mo)
   ──────────────────────────────────────────────────────────────── */

export type UpgradeIntent = 'money' | 'skill' | null;

const MONEY_KEYWORDS = [
  'income',
  'money',
  'price',
  'pricing',
  'charge',
  'charging',
  'rate',
  'rates',
  'rebook',
  'retention',
  'client retention',
  'new client',
  'new clients',
  'more clients',
  'book more',
  'fully booked',
  'cash flow',
  'cashflow',
  'revenue',
  'profit',
  'business',
  'salon owner',
  'booth rent',
  'commission',
  'tips',
  'upsell',
  'package',
  'membership',
  'instagram',
  'social media',
  'marketing',
  'grow my',
  'growth',
  'compete',
  'competition',
  'leaderboard',
  'visibility',
];

const SKILL_KEYWORDS = [
  'color',
  'colour',
  'formula',
  'formulas',
  'mix',
  'mixing',
  'developer',
  'volume',
  'toner',
  'tone',
  'toning',
  'lift',
  'lifting',
  'level',
  'bleach',
  'balayage',
  'highlight',
  'highlights',
  'foilyage',
  'gloss',
  'glaze',
  'cut',
  'cutting',
  'haircut',
  'layers',
  'layering',
  'fringe',
  'bangs',
  'bob',
  'lob',
  'shag',
  'pixie',
  'curly',
  'texture',
  'consultation',
  'consult',
  'rescue',
  'fix',
  'correction',
  'brassy',
  'orange',
  'banding',
  'breakage',
  'porosity',
  'perm',
  'relaxer',
  'keratin',
  'olaplex',
  'box dye',
  'henna',
];

export function detectUpgradeIntent(input: string): UpgradeIntent {
  if (!input) return null;
  const lower = input.toLowerCase();
  const moneyHits = MONEY_KEYWORDS.filter((kw) => lower.includes(kw)).length;
  const skillHits = SKILL_KEYWORDS.filter((kw) => lower.includes(kw)).length;
  if (moneyHits === 0 && skillHits === 0) return null;
  // Money signals are rarer & higher-intent — prefer money on tie.
  if (moneyHits >= skillHits) return 'money';
  return 'skill';
}

const STORAGE_KEY = 'hpe_upgrade_intent';

export function rememberUpgradeIntent(intent: UpgradeIntent): void {
  if (typeof window === 'undefined') return;
  if (!intent) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, intent);
  } catch {
    /* no-op */
  }
}

export function getRememberedUpgradeIntent(): UpgradeIntent {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === 'money' || v === 'skill' ? v : null;
  } catch {
    return null;
  }
}

/** Convenience: detect from text and persist if found. */
export function captureUpgradeIntent(input: string): UpgradeIntent {
  const intent = detectUpgradeIntent(input);
  if (intent) rememberUpgradeIntent(intent);
  return intent;
}

/* ────────────────────────────────────────────────────────────────
   Questionnaire-driven intent
   The onboarding questionnaire asks discrete options (improvements +
   biggest goal). Map them to upgrade intent. Money signals win on tie
   so growth-oriented users get pushed to Pro Competitor.
   ──────────────────────────────────────────────────────────────── */

const MONEY_GOAL_OPTIONS = new Set(
  [
    // GOALS
    'get more clients',
    'raise my income',
    'grow my brand',
    'build a team',
    'open my own space',
    // IMPROVEMENTS
    'pricing',
    'attracting new clients',
    'client retention',
    'rebooking',
    'upselling',
    'team building',
    'content creation',
  ].map((s) => s.toLowerCase()),
);

const SKILL_GOAL_OPTIONS = new Set(
  [
    // GOALS
    'improve my skills',
    'build confidence',
    // IMPROVEMENTS
    'confidence',
    'speed',
    'formulation',
  ].map((s) => s.toLowerCase()),
);

export interface QuestionnaireSignal {
  improvements?: string[];
  goal?: string | null;
}

export function detectUpgradeIntentFromGoals(sig: QuestionnaireSignal): UpgradeIntent {
  const tokens: string[] = [];
  if (sig.goal) tokens.push(sig.goal.toLowerCase());
  if (sig.improvements) tokens.push(...sig.improvements.map((s) => s.toLowerCase()));
  if (tokens.length === 0) return null;

  let money = 0;
  let skill = 0;
  for (const t of tokens) {
    if (MONEY_GOAL_OPTIONS.has(t)) money++;
    else if (SKILL_GOAL_OPTIONS.has(t)) skill++;
  }
  if (money === 0 && skill === 0) return null;
  if (money >= skill) return 'money';
  return 'skill';
}

/** Capture questionnaire selections as upgrade intent + mark questionnaire engaged. */
export function captureQuestionnaireIntent(sig: QuestionnaireSignal): UpgradeIntent {
  const intent = detectUpgradeIntentFromGoals(sig);
  if (intent) rememberUpgradeIntent(intent);
  return intent;
}

/* ────────────────────────────────────────────────────────────────
   Checkout gate
   Rule: "Do not show checkout before answer + questionnaire flow."
   Gate passes when BOTH are true:
     - hpe_answer_seen          → set after the user has seen an answer
     - hpe_questionnaire_started → set when they begin or complete it
   ──────────────────────────────────────────────────────────────── */

const ANSWER_SEEN_KEY = 'hpe_answer_seen';
const QUESTIONNAIRE_KEY = 'hpe_questionnaire_started';

export function markAnswerSeen(): void {
  if (typeof window === 'undefined') return;
  try { window.localStorage.setItem(ANSWER_SEEN_KEY, '1'); } catch { /* no-op */ }
}

export function markQuestionnaireStarted(): void {
  if (typeof window === 'undefined') return;
  try { window.localStorage.setItem(QUESTIONNAIRE_KEY, '1'); } catch { /* no-op */ }
}

export function hasSeenAnswer(): boolean {
  if (typeof window === 'undefined') return false;
  try { return window.localStorage.getItem(ANSWER_SEEN_KEY) === '1'; } catch { return false; }
}

export function hasStartedQuestionnaire(): boolean {
  if (typeof window === 'undefined') return false;
  try { return window.localStorage.getItem(QUESTIONNAIRE_KEY) === '1'; } catch { return false; }
}

export function canAccessCheckout(): boolean {
  return hasSeenAnswer() && hasStartedQuestionnaire();
}

